// PitchDetailPage.jsx
// Auth-gated view of a single pitch by its Firestore document ID.
// Routed at /pitch/:pitchId. Only authenticated users with a portal role
// (lp, chapter_director, superAdmin) can view — this is the page the
// "Review Application" Slack button links to.
//
// Unauthenticated visitors are bounced to /portal with a sessionStorage
// breadcrumb (PENDING_PITCH_URL_KEY) that the LP portal's auth handler
// reads after successful login to return them here automatically.
import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebaseConfig";
import NotFoundPage from "./NotFoundPage";
import PageTaskbar from "../components/ui/Taskbar/PageTaskbar";
import OSIcon32 from "../components/icons/OSIcon32";
import { externalVideoHost, safeVideoUrl, uploadedVideoSrc } from "../utils/safeVideoUrl";
import { VALID_ROLES } from "../data/roles";

export const PENDING_PITCH_URL_KEY = "pendingPitchUrl";

export default function PitchDetailPage() {
  const { pitchId } = useParams();
  const navigate = useNavigate();
  // auth-loading | unauthenticated | forbidden | loading | found | not-found
  const [status, setStatus] = useState("auth-loading");
  const [pitch, setPitch] = useState(null);

  // Auth check: only valid portal roles can view. Unauthed → stash pitch URL
  // and redirect to /portal so they can land back here after logging in.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        setStatus("unauthenticated");
        return;
      }
      try {
        const userSnap = await getDoc(doc(db, "users", authUser.uid));
        const role = userSnap.exists() ? userSnap.data().role : null;
        if (role && VALID_ROLES.includes(role)) {
          setStatus("loading");
        } else {
          setStatus("forbidden");
        }
      } catch (e) {
        console.error("PitchDetailPage: role lookup failed", e);
        setStatus("forbidden");
      }
    });
    return unsubscribe;
  }, []);

  // Pitch fetch runs only after the auth gate passes.
  useEffect(() => {
    if (status !== "loading") return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "pitches", pitchId));
        if (cancelled) return;
        if (snap.exists()) {
          setPitch({ id: snap.id, ...snap.data() });
          setStatus("found");
        } else {
          setStatus("not-found");
        }
      } catch (e) {
        console.error("PitchDetailPage: failed to load pitch", e);
        if (!cancelled) setStatus("not-found");
      }
    })();
    return () => { cancelled = true; };
  }, [status, pitchId]);

  if (status === "auth-loading" || status === "loading") {
    return (
      <div className="mb-page" style={pageStyle}>
        <PageTaskbar windowLabel="Pitch detail" windowIcon="pitch" />
        <p style={{ textAlign: "center", color: "var(--mb-ink)", fontWeight: 600, fontSize: 14, marginTop: 80 }}>
          {status === "auth-loading" ? "Checking access…" : "Loading pitch…"}
        </p>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return (
      <GateScreen
        title="Sign in required"
        body="This pitch is only viewable by Good Neighbor Fund Limited Partners. Sign in to continue."
        cta="Sign in to LP portal"
        onCta={() => {
          try {
            sessionStorage.setItem(PENDING_PITCH_URL_KEY, `/pitch/${pitchId}`);
          } catch {}
          navigate("/portal");
        }}
      />
    );
  }

  if (status === "forbidden") {
    return (
      <GateScreen
        title="Access denied"
        body="Your account doesn't have permission to view pitch applications. If you think this is a mistake, contact your chapter director."
        cta="Return to home"
        onCta={() => navigate("/")}
      />
    );
  }

  if (status === "not-found") {
    return <NotFoundPage />;
  }

  const submittedAt = formatTimestamp(pitch.createdAt);

  return (
    <div className="mb-page" style={pageStyle}>
      <PageTaskbar windowLabel="Pitch detail" windowIcon="pitch" />
      <div className="mb-form-shell" style={chromeWindowStyle}>
        <div className="win95-titlebar" style={{ cursor: "default", flexShrink: 0 }}>
          <OSIcon32 kind="pitch" size={15} className="win95-titlebar__icon" />
          <span className="win95-titlebar__label">Pitch detail</span>
          <div className="win95-titlebar__btns">
            <button
              type="button"
              className="win95-btn-sys win95-close-btn"
              onClick={() => navigate("/portal")}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div style={bodyStyle}>
          <div style={headerStyle}>
            <div style={eyebrowStyle}>
              {pitch.chapter || "Good Neighbor Fund"}
              {submittedAt ? ` · Submitted ${submittedAt}` : ""}
              {pitch.isWinner && pitch.winnerPublished !== false ? " · Grant recipient" : ""}
            </div>
            <h1 style={titleStyle}>{pitch.businessName || "Untitled pitch"}</h1>
            {pitch.founderName && (
              <p style={subtitleStyle}>by {pitch.founderName}</p>
            )}
            {pitch.email && (
              <p style={{ ...subtitleStyle, fontSize: 14, marginTop: 4 }}>
                <a href={`mailto:${pitch.email}`} style={linkStyle}>{pitch.email}</a>
              </p>
            )}
          </div>

          {(() => {
            // The video URL came from the public submission form. Validate it
            // before linking (defense in depth — docs already stored were
            // never validated at submit time). Only our own uploads play
            // inline; third-party pages are never framed here, and their
            // links name the host they lead to.
            const videoLink = safeVideoUrl(pitch.pitchVideoUrl) || safeVideoUrl(pitch.pitchVideoFile);
            if (!videoLink) return null;
            const uploadedSrc = uploadedVideoSrc(videoLink);
            const externalHost = externalVideoHost(videoLink);
            return (
              <Section title="Pitch video">
                {uploadedSrc && (
                  <div style={videoWrapperStyle}>
                    <video
                      src={uploadedSrc}
                      aria-label={`${pitch.businessName || "Pitch"} video`}
                      controls
                      playsInline
                      preload="metadata"
                      style={{ display: "block", width: "100%", height: "100%" }}
                    />
                  </div>
                )}
                <a
                  href={videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  {externalHost ? `Open video on ${externalHost} ↗` : "Open video in new tab ↗"}
                </a>
              </Section>
            );
          })()}

          {pitch.bio && (
            <Section title="About the founder">
              <p style={paragraphStyle}>{pitch.bio}</p>
            </Section>
          )}

          {pitch.aiSummary && (
            <Section title="AI summary">
              <p style={{ ...paragraphStyle, background: "var(--mb-magenta-soft)", padding: 12 }}>
                {pitch.aiSummary}
              </p>
            </Section>
          )}

          {pitch.valueProp && (
            <Section title="Value proposition">
              <p style={paragraphStyle}>{pitch.valueProp}</p>
            </Section>
          )}

          {pitch.problem && (
            <Section title="Problem">
              <p style={paragraphStyle}>{pitch.problem}</p>
            </Section>
          )}

          {pitch.solution && (
            <Section title="Solution">
              <p style={paragraphStyle}>{pitch.solution}</p>
            </Section>
          )}

          {pitch.businessModel && (
            <Section title="Business model">
              <p style={paragraphStyle}>{pitch.businessModel}</p>
            </Section>
          )}

          {pitch.grantUsePlan && (
            <Section title="Plan for $1,000 grant">
              <p style={paragraphStyle}>{pitch.grantUsePlan}</p>
            </Section>
          )}

          <Section title="Details">
            <dl style={detailsGridStyle}>
              {pitch.chapter && <DetailRow label="Chapter" value={pitch.chapter} />}
              {pitch.zipCode && <DetailRow label="ZIP code" value={pitch.zipCode} />}
              {pitch.website && (
                <DetailRow
                  label="Website"
                  value={
                    <a
                      href={pitch.website.startsWith("http") ? pitch.website : `https://${pitch.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                    >
                      {pitch.website}
                    </a>
                  }
                />
              )}
              {pitch.hasPayingCustomers && (
                <DetailRow label="Has paying customers" value={pitch.hasPayingCustomers} />
              )}
              {pitch.heardAbout && <DetailRow label="Heard about us via" value={pitch.heardAbout} />}
              {Array.isArray(pitch.selfIdentification) && pitch.selfIdentification.length > 0 && (
                <DetailRow label="Self identification" value={pitch.selfIdentification.join(", ")} />
              )}
            </dl>
          </Section>

          <div style={footerStyle}>
            <span style={{ fontSize: 12, color: "var(--mb-ink-60)" }}>Pitch ID: {pitch.id}</span>
            <button
              onClick={() => navigate("/portal")}
              type="button"
              className="mb-btn"
            >
              Back to portal
              <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GateScreen({ title, body, cta, onCta }) {
  return (
    <div className="mb-page" style={pageStyle}>
      <PageTaskbar windowLabel="Pitch detail" windowIcon="pitch" />
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 90px)",
      }}>
        <div style={{ ...chromeWindowStyle, width: "100%", maxWidth: 480, textAlign: "center" }}>
          <div className="win95-titlebar" style={{ cursor: "default" }}>
            <OSIcon32 kind="pitch" size={15} className="win95-titlebar__icon" />
            <span className="win95-titlebar__label" style={{ textAlign: "left" }}>Pitch detail</span>
          </div>
          <div style={{ padding: "36px 32px 30px" }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>Limited Partner access</span>
            <h1 style={{ ...titleStyle, margin: "12px 0 12px" }}>{title}</h1>
            <p style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 24 }}>{body}</p>
            <button onClick={onCta} type="button" className="mb-btn">
              {cta}
              <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {children}
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <>
      <dt style={dtStyle}>{label}</dt>
      <dd style={ddStyle}>{value}</dd>
    </>
  );
}

function formatTimestamp(createdAt) {
  if (!createdAt) return "";
  try {
    const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

const pageStyle = {
  minHeight: "100vh",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  padding: "10px",
  paddingBottom: "calc(80px + env(safe-area-inset-bottom))",
  background: "transparent",
  fontFamily: "var(--font-content)",
  color: "var(--mb-ink)",
};

// Chrome window shell — the standalone page reads as a maximized OS window.
const chromeWindowStyle = {
  background: "var(--chrome-face)",
  border: "2px solid",
  borderColor: "var(--bevel-raised)",
  boxShadow: "var(--shadow-raised), var(--window-drop)",
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  width: "95%",
  maxWidth: 720,
};

const bodyStyle = {
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
};

const headerStyle = {
  borderBottom: "1px solid var(--chrome-shade)",
  boxShadow: "0 1px 0 var(--chrome-highlight)",
  paddingBottom: "16px",
};

const eyebrowStyle = {
  fontSize: "11px",
  fontFamily: "var(--font-chrome)",
  fontWeight: 700,
  letterSpacing: "var(--track-label)",
  textTransform: "none",
  color: "var(--mb-magenta)",
  marginBottom: "8px",
};

const titleStyle = {
  fontFamily: "var(--font-display)",
  fontWeight: 700,
  fontSize: 28,
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  margin: "4px 0 8px",
};

const subtitleStyle = {
  fontSize: 16,
  margin: 0,
  color: "var(--mb-ink)",
};

const sectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const sectionTitleStyle = {
  fontFamily: "var(--font-display)",
  fontSize: 20,
  fontWeight: 600,
  margin: 0,
  color: "var(--mb-ink)",
};

const paragraphStyle = {
  fontSize: 15,
  lineHeight: 1.55,
  margin: 0,
  whiteSpace: "pre-wrap",
};

const videoWrapperStyle = {
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 9",
  border: "2px solid",
  borderColor: "var(--bevel-sunken)",
  background: "var(--mb-ink)",
  overflow: "hidden",
};

const linkStyle = {
  color: "var(--mb-magenta)",
  fontWeight: "bold",
  textDecoration: "underline",
};

const detailsGridStyle = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: "16px",
  rowGap: "6px",
  margin: 0,
  fontSize: 14,
};

const dtStyle = { fontWeight: "bold" };
const ddStyle = { margin: 0 };

const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "12px",
  paddingTop: "16px",
  borderTop: "1px solid var(--chrome-shade)",
};
